import { AxiosResponse } from 'axios';
import { useCallback, useRef } from 'react';
import { useRecoilState } from 'recoil';

import ApiService from '../modules/api-service';
import { MonetaryAmountStrict } from '../modules/currency';
import {
  CurrencyCode,
  SourceRegisterPotentialDTO,
  SourceType,
  SupportedPriceType,
  ValuationCountryCode,
} from '../modules/generated/api';
import { packageBidPotentialCalculationsState } from '../stores/state';

export type FetchCalculationPackageParams = {
  fixedVariable: SupportedPriceType;
  value?: MonetaryAmountStrict;
  valuationCountry?: ValuationCountryCode;
  bidNumber: 1 | 2 | 3;
};

export const useCalculationPackage = (source: SourceType, packageId: string) => {
  const [calculations, setCalculations] = useRecoilState(packageBidPotentialCalculationsState);
  const lastPromise = useRef<{
    1: Promise<AxiosResponse<SourceRegisterPotentialDTO>> | undefined;
    2: Promise<AxiosResponse<SourceRegisterPotentialDTO>> | undefined;
    3: Promise<AxiosResponse<SourceRegisterPotentialDTO>> | undefined;
  }>({ 1: undefined, 2: undefined, 3: undefined });

  const fetchCalculation = useCallback(
    async ({ fixedVariable, value, valuationCountry, bidNumber }: FetchCalculationPackageParams) => {
      const promise = value
        ? ApiService.packageApi.priceCalculationControllerCalculatePackagePrice(
            source,
            packageId,
            fixedVariable,
            value.amount,
            value.currency as CurrencyCode,
            valuationCountry,
          )
        : null;
      lastPromise.current = { ...lastPromise.current, [bidNumber]: promise };
      if (promise === null) return undefined;
      const response = await promise;
      if (lastPromise.current[bidNumber] !== promise) return null;

      setCalculations((prev) => ({ ...prev, [bidNumber]: response.data }));
      return response.data;
    },
    [source, packageId, setCalculations],
  );
  return { calculations, fetchCalculation };
};
